import { Stack } from '@mui/material';
import { useEffect, useMemo } from 'react';

import { UsersPlusIcon } from '@/components/icons/UsersPlusIcon';
import { areAllHeaderListGroupsEmpty } from '@/components/lists/GroupedHeaderList/GroupedHeaderList.utils';
import { EmptyListEditEntityActionCard } from '@/modules/entities/components/EmptyListEditEntityActionCard';
import { isCurrentBeneficiaryEntityKind } from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.constants';
import { EntitySubformCategory } from '@/modules/entities/EntitySubforms/entitySubformConfigs';
import { EntityKind } from '@/types/schema';

import { ExpandableBeneficiaryTable } from './ExpandableBeneficiaryTable';
import { FlatBeneficiaryTable } from './FlatBeneficiaryTable';
import { TrustBeneficiariesListProps } from './TrustBeneficiariesList';

function getEmptyBeneficiariesLabels(
  entityKind: EntityKind | undefined,
  isTestamentaryEntity: boolean
): {
  heading: string;
  buttonText: string;
  description: string;
} {
  if (
    entityKind === EntityKind.QprtTrust ||
    entityKind === EntityKind.GratTrust
  ) {
    return {
      heading: 'Specify remainder beneficiaries',
      buttonText: 'Add remainder beneficiaries',
      description: 'No beneficiaries have been specified for this entity',
    };
  }

  if (isCurrentBeneficiaryEntityKind(entityKind)) {
    return {
      heading: 'Specify current beneficiaries',
      buttonText: 'Add individuals',
      description:
        'Specify the individuals that currently have access to this entity',
    };
  }

  if (entityKind === EntityKind.CltTrust) {
    return {
      heading: 'Specify lead beneficiary',
      buttonText: 'Add lead beneficiary',
      description: 'No lead beneficiary has been specified for this entity',
    };
  }

  if (entityKind === EntityKind.CrtTrust) {
    return {
      heading: 'Specify income beneficiary',
      buttonText: 'Add income beneficiary',
      description: 'No income beneficiary has been specified for this entity',
    };
  }

  if (isTestamentaryEntity || entityKind === EntityKind.SlatTrust) {
    return {
      heading: 'Specify lifetime beneficiaries',
      buttonText: 'Add lifetime beneficiaries',
      description: 'No beneficiaries have been specified for this entity',
    };
  }

  return {
    heading: 'Specify current beneficiaries',
    buttonText: 'Add current beneficiaries',
    description: 'No beneficiaries have been specified for this entity',
  };
}

export function TrustBeneficiariesListV2({
  beneficiariesGroups,
  variant = 'expandable',
  isTestamentaryEntity,
  onShowEditEntity,
  entityKind,
  rows,
  emptyStateLabels,
  testamentaryEntityInitialTab,
}: TrustBeneficiariesListProps) {
  const hasBeneficiaries = !areAllHeaderListGroupsEmpty(beneficiariesGroups);

  useEffect(() => {
    if (hasBeneficiaries && variant === 'expandable') {
      onShowEditEntity?.(true);
    } else {
      onShowEditEntity?.(false);
    }
  }, [hasBeneficiaries, onShowEditEntity, variant]);

  const TableComponent = useMemo(
    () =>
      variant === 'flat' ? FlatBeneficiaryTable : ExpandableBeneficiaryTable,
    [variant]
  );

  if (variant === 'expandable' && !hasBeneficiaries) {
    const labels =
      emptyStateLabels ??
      getEmptyBeneficiariesLabels(entityKind, isTestamentaryEntity ?? false);

    return (
      <EmptyListEditEntityActionCard
        {...labels}
        icon={UsersPlusIcon}
        editSubformCategory={EntitySubformCategory.BENEFICIARIES}
        testamentaryEntityInitialTab={testamentaryEntityInitialTab}
      />
    );
  }

  return (
    <Stack spacing={2}>
      {beneficiariesGroups.map((group, idx) => (
        <TableComponent
          key={group.heading ?? `beneficiary-group-${idx}`}
          beneficiaries={group.items}
          heading={group.heading}
          entityKind={entityKind}
          rows={rows}
        />
      ))}
    </Stack>
  );
}
